import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router"
import { useApplication } from "../../store/application/hooks"
import { renderer } from "../viewer/Renderer"
import { Models } from "../viewer/renderer.api"
import { OverviewTab } from "./OverviewTab"
import { RegularTab } from "./RegularTab"
import { Tab } from "./Tabs"
import { useRoutes } from "../../store/routes/hooks"
import { SolInsulationTypes } from "@iko-design-center/shared"
import { useFlatRoof } from "../../store/flat-roof/hooks"
import { useCavityWall } from "../../store/cavity-wall/hooks"
import { useSlopedRoof } from "../../store/sloped-roof/hooks"
import { useVentilatedWall } from "../../store/ventilated-wall/hooks"
import { useFloor } from "../../store/floor/hooks"
import { useSol } from "../../store/sol/hooks"
import { useToitEnPente } from "../../store/toit-en-pente/hooks"
import {
  getRoofTypeAndClassTranslations as getFlatRoofTypeTranslations,
  getRoofFloorTranslations,
  getWaterproofingTranslations,
  getSecuringMethodTranslations,
} from "../sidebar/flat-roof/translations"
import {
  getWallInsulationTranslations,
  getWallTypeTranslations as getCavityWallTypeTranslations,
} from "../sidebar/cavity-wall/translations"
import {
  getRoofInsulationTranslations,
  getRoofTypeTranslations as getSlopedRoofTypeTranslations,
} from "../sidebar/sloped-roof/translations"
import {
  getFacadeCladdingTranslations,
  getWallTypeTranslations as getVentilatedWallTypeTranslations,
} from "../sidebar/ventilated-wall/translations"
import {
  getFloorHeatingTranslations,
  getFloorInsulationOptionTranslations,
  getFloorScreedTranslations,
} from "../sidebar/floor/translations"
import {
  getSolAcousticLayerTranslations,
  getSolBuildingTypeTranslations,
  getSolConformityTranslations,
  getSolInsulationTranslations,
  getSolNoiseLevelTranslations,
} from "../sidebar/sol/translations"
import { useMatrix } from "../sidebar/flat-roof/matrix.hook"
import {
  getToitEnPenteRainCoverTranslations,
  getToitEnPenteStructureTranslations,
} from "../sidebar/toit-en-pente/translations"

export function useTabs() {
  const { Routes } = useRoutes() as any
  const routeMap = new Map<string, number>([
    // GENERAL
    [Routes.HOME, 0],
    // FLAT ROOF
    [Routes.FLAT_ROOF_1_ROOF_TYPE, 1],
    [Routes.FLAT_ROOF_2_ROOF_FLOOR, 2],
    [Routes.FLAT_ROOF_3_WATERPROOFING, 3],
    [Routes.FLAT_ROOF_4_SECURING_METHODS, 4],
    [Routes.FLAT_ROOF_5_OVERVIEW, 5],
    // CAVITY WALL
    [Routes.CAVITY_WALL_1_WALL_TYPE, 1],
    [Routes.CAVITY_WALL_2_WALL_INSULATION, 2],
    [Routes.CAVITY_WALL_3_OVERVIEW, 5],
    // SLOPED ROOF
    [Routes.SLOPED_ROOF_1_ROOF_TYPE, 1],
    [Routes.SLOPED_ROOF_2_ROOF_INSULATION, 2],
    [Routes.SLOPED_ROOF_3_OVERVIEW, 3],
    // VENTILATED WALL
    [Routes.VENTILATED_WALL_1_WALL_TYPE, 1],
    [Routes.VENTILATED_WALL_2_FACADE_CLADDING, 2],
    [Routes.VENTILATED_WALL_3_OVERVIEW, 3],
    // FLOOR
    [Routes.FLOOR_1_FLOOR_HEATING, 1],
    [Routes.FLOOR_2_FLOOR_INSULATION, 2],
    [Routes.FLOOR_3_FLOOR_SCREED, 3],
    [Routes.FLOOR_4_OVERVIEW, 4],
    // SOL
    [Routes.SOL_1_INSULATION, 1],
    [Routes.SOL_2_NOISE_LEVEL, 2],
    [Routes.SOL_3_ACOUSTIC_LAYER, 3],
    [Routes.SOL_2_CONFORMITY, 2],
    [Routes.SOL_3_BUILDING_TYPE, 3],
    [Routes.SOL_4_OVERVIEW, 4],
    // TOIT EN PENTE
    [Routes.TOIT_EN_PENTE_1_STRUCTURE, 1],
    [Routes.TOIT_EN_PENTE_2_RAIN_COVER, 2],
    [Routes.TOIT_EN_PENTE_3_OVERVIEW, 3],
  ])

  const { t, i18n } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const pathname = location.pathname
  const currentStep = routeMap.get(pathname as string)!
  const country = i18n.language.split("_")[1]
  const { applicationType } = useApplication()
  const { flatRoofState } = useFlatRoof()
  const { cavityWallState } = useCavityWall()
  const { slopedRoofState } = useSlopedRoof()
  const { ventilatedWallState } = useVentilatedWall()
  const { floorState } = useFloor()
  const { solState } = useSol()
  const { toitEnPenteState } = useToitEnPente()
  const { displayDefaultFlatRoofCombination } = useMatrix()

  // FLAT ROOF
  function displayFlatRoof() {
    displayDefaultFlatRoofCombination()
  }

  function navigateToRoofType() {
    if (currentStep >= 1) {
      history.push(Routes.FLAT_ROOF_1_ROOF_TYPE)
      displayFlatRoof()
    }
  }

  function navigateToRoofFloor() {
    if (currentStep >= 2) {
      history.push(Routes.FLAT_ROOF_2_ROOF_FLOOR)
      displayFlatRoof()
    }
  }

  function navigateToWaterproofing() {
    if (currentStep >= 3) {
      history.push(Routes.FLAT_ROOF_3_WATERPROOFING)
      displayFlatRoof()
    }
  }

  function navigateToSecuringMethods() {
    if (currentStep >= 4) {
      history.push(Routes.FLAT_ROOF_4_SECURING_METHODS)
      displayFlatRoof()
    }
  }

  function navigateToFlatRoofOverview() {
    if (currentStep >= 5) {
      history.push(Routes.FLAT_ROOF_5_OVERVIEW)
      displayFlatRoof()
    }
  }

  // CAVITY WALL
  function displayCavityWall() {
    renderer.displayModel(Models.CAVITY_WALL).then(() => {
      renderer.toggleAllLayers(applicationType, true)
    })
  }

  function navigateToWallType() {
    if (currentStep >= 1) {
      history.push(Routes.CAVITY_WALL_1_WALL_TYPE)
      displayCavityWall()
    }
  }

  function navigateToWallInsulation() {
    if (currentStep >= 2) {
      history.push(Routes.CAVITY_WALL_2_WALL_INSULATION)
      displayCavityWall()
    }
  }

  function navigateToCavityWallOverview() {
    if (currentStep >= 3) {
      history.push(Routes.CAVITY_WALL_3_OVERVIEW)
      displayCavityWall()
    }
  }

  // SLOPED ROOF
  function displaySlopedRoof() {
    renderer.displayModel(Models.SLOPED_ROOF).then(() => {
      renderer.toggleAllLayers(applicationType, true)
    })
  }

  function navigateToSlopedRoofType() {
    if (currentStep >= 1) {
      history.push(Routes.SLOPED_ROOF_1_ROOF_TYPE)
      displaySlopedRoof()
    }
  }

  function navigateToSlopedRoofInsulation() {
    if (currentStep >= 2) {
      history.push(Routes.SLOPED_ROOF_2_ROOF_INSULATION)
      displaySlopedRoof()
    }
  }

  function navigateToSlopedRoofOverview() {
    if (currentStep >= 3) {
      history.push(Routes.SLOPED_ROOF_3_OVERVIEW)
      displaySlopedRoof()
    }
  }

  // VENTILATED WALL
  function displayVentilatedWall() {
    renderer.displayModel(Models.VENTILATED_WALL).then(() => {
      renderer.toggleAllLayers(applicationType, true)
    })
  }

  function navigateToVentilatedWallWallType() {
    if (currentStep >= 1) {
      history.push(Routes.VENTILATED_WALL_1_WALL_TYPE)
      displayVentilatedWall()
    }
  }

  function navigateToVentilatedWallFacadeCladding() {
    if (currentStep >= 2) {
      history.push(Routes.VENTILATED_WALL_2_FACADE_CLADDING)
      displayVentilatedWall()
    }
  }

  function navigateToVentilatedWallOverview() {
    if (currentStep >= 3) {
      history.push(Routes.VENTILATED_WALL_3_OVERVIEW)
      displayVentilatedWall()
    }
  }

  // FLOOR
  function displayFloor() {
    renderer.displayModel(Models.FLOOR).then(() => {
      renderer.toggleAllLayers(applicationType, true)
    })
  }

  function navigateToFloorHeating() {
    if (currentStep >= 1) {
      history.push(Routes.FLOOR_1_FLOOR_HEATING)
      displayFloor()
    }
  }

  function navigateToFloorInsulation() {
    if (currentStep >= 2) {
      history.push(Routes.FLOOR_2_FLOOR_INSULATION)
      displayFloor()
    }
  }

  function navigateToFloorScreed() {
    if (currentStep >= 2) {
      history.push(Routes.FLOOR_3_FLOOR_SCREED)
      displayFloor()
    }
  }

  function navigateToFloorOverview() {
    if (currentStep >= 3) {
      history.push(Routes.FLOOR_4_OVERVIEW)
      displayFloor()
    }
  }

  // SOL
  function displaySol() {
    renderer.toggleAllLayers(applicationType, true)
  }

  function navigateToSolInsulation() {
    if (currentStep >= 1) {
      history.push(Routes.SOL_1_INSULATION)
      displaySol()
    }
  }

  function navigateToSolNoiseLevel() {
    if (currentStep >= 2) {
      history.push(Routes.SOL_2_NOISE_LEVEL)
      displaySol()
    }
  }

  function navigateToSolAcousticLayer() {
    if (currentStep >= 3) {
      history.push(Routes.SOL_3_ACOUSTIC_LAYER)
      displaySol()
    }
  }

  function navigateToSolConformity() {
    if (currentStep >= 2) {
      history.push(Routes.SOL_2_CONFORMITY)
      displaySol()
    }
  }

  function navigateToSolBuildingType() {
    if (currentStep >= 3) {
      history.push(Routes.SOL_3_BUILDING_TYPE)
      displaySol()
    }
  }

  function navigateToSolOverview() {
    if (currentStep >= 4) {
      history.push(Routes.SOL_4_OVERVIEW)
      displaySol()
    }
  }

  // TOIT EN PENTE
  function displayToitEnPente() {
    renderer.toggleAllLayers(applicationType, true)
  }

  function navigateToToitEnPenteStructure() {
    if (currentStep >= 1) {
      history.push(Routes.TOIT_EN_PENTE_1_STRUCTURE)
      displayToitEnPente()
    }
  }

  function navigateToToitEnPenteRainCover() {
    if (currentStep >= 2) {
      history.push(Routes.TOIT_EN_PENTE_2_RAIN_COVER)
      displayToitEnPente()
    }
  }

  function navigateToToitEnPenteOverview() {
    if (currentStep >= 3) {
      history.push(Routes.TOIT_EN_PENTE_3_OVERVIEW)
      displayToitEnPente()
    }
  }

  // FLAT ROOF
  const flatRoofTabs: Tab[] = [
    {
      step: 1,
      component: RegularTab,
      text: t("navigation.flatRoof.roofType"),
      selected: getFlatRoofTypeTranslations(
        t,
        flatRoofState.configuration.roofType.type,
        flatRoofState.configuration.roofType.climateClass,
      ),
      active: pathname === Routes.FLAT_ROOF_1_ROOF_TYPE,
      visited: 1 <= currentStep,
      onClick: navigateToRoofType,
    },
    {
      step: 2,
      component: RegularTab,
      text: t("navigation.flatRoof.roofFloor"),
      selected: getRoofFloorTranslations(
        t,
        flatRoofState.configuration.roofFloor.type,
      ),
      active: pathname === Routes.FLAT_ROOF_2_ROOF_FLOOR,
      visited: 2 <= currentStep,
      onClick: navigateToRoofFloor,
    },
    {
      step: 3,
      component: RegularTab,
      text: t("navigation.flatRoof.waterproofing"),
      selected: getWaterproofingTranslations(
        t,
        flatRoofState.configuration.waterproofing.type,
      ),
      active: pathname === Routes.FLAT_ROOF_3_WATERPROOFING,
      visited: 3 <= currentStep,
      onClick: navigateToWaterproofing,
    },
    {
      step: 4,
      component: RegularTab,
      text: t("navigation.flatRoof.securingMethods"),
      selected: getSecuringMethodTranslations(
        t,
        flatRoofState.configuration.securingMethods,
      ),
      active: pathname === Routes.FLAT_ROOF_4_SECURING_METHODS,
      visited: 4 <= currentStep,
      onClick: navigateToSecuringMethods,
    },
    {
      step: 5,
      component: OverviewTab,
      text: t("navigation.flatRoof.overview"),
      selected: null,
      active: pathname === Routes.FLAT_ROOF_5_OVERVIEW,
      visited: false,
      onClick: navigateToFlatRoofOverview,
    },
  ]

  // CAVITY WALL
  const cavityWallTabs: Tab[] = [
    {
      step: 1,
      component: RegularTab,
      text: t("navigation.cavityWall.wallType" as any),
      selected: getCavityWallTypeTranslations(
        t,
        cavityWallState.configuration.wallType.type,
      ),
      active: pathname === Routes.CAVITY_WALL_1_WALL_TYPE,
      visited: 1 <= currentStep,
      onClick: navigateToWallType,
    },
    {
      step: 2,
      component: RegularTab,
      text: t("navigation.cavityWall.wallInsulation" as any),
      selected: getWallInsulationTranslations(
        t,
        cavityWallState.configuration.wallInsulation.type,
      ),
      active: pathname === Routes.CAVITY_WALL_2_WALL_INSULATION,
      visited: 2 <= currentStep,
      onClick: navigateToWallInsulation,
    },
    {
      step: 3,
      component: OverviewTab,
      text: t("navigation.cavityWall.overview" as any),
      selected: null,
      active: pathname === Routes.CAVITY_WALL_3_OVERVIEW,
      visited: 3 <= currentStep,
      onClick: navigateToCavityWallOverview,
    },
  ]

  // SLOPED ROOF
  const slopedRoofTabs: Tab[] = [
    {
      step: 1,
      component: RegularTab,
      text: t("navigation.slopedRoof.roofType" as any),
      selected: getSlopedRoofTypeTranslations(
        t,
        slopedRoofState.configuration.roofType.type,
      ),
      active: pathname === Routes.SLOPED_ROOF_1_ROOF_TYPE,
      visited: 1 <= currentStep,
      onClick: navigateToSlopedRoofType,
    },
    {
      step: 2,
      component: RegularTab,
      text: t("navigation.slopedRoof.roofInsulation" as any),
      selected: getRoofInsulationTranslations(
        t,
        slopedRoofState.configuration.roofInsulation.type,
      ),
      active: pathname === Routes.SLOPED_ROOF_2_ROOF_INSULATION,
      visited: 2 <= currentStep,
      onClick: navigateToSlopedRoofInsulation,
    },
    {
      step: 3,
      component: OverviewTab,
      text: t("navigation.slopedRoof.overview" as any),
      selected: null,
      active: pathname === Routes.SLOPED_ROOF_3_OVERVIEW,
      visited: 3 <= currentStep,
      onClick: navigateToSlopedRoofOverview,
    },
  ]

  // VENTILATED WALL
  const ventilatedWallTabs: Tab[] = [
    {
      step: 1,
      component: RegularTab,
      text: t("navigation.ventilatedWall.wallType" as any),
      selected: getVentilatedWallTypeTranslations(
        t,
        ventilatedWallState.configuration.wallType.type,
      ),
      active: pathname === Routes.VENTILATED_WALL_1_WALL_TYPE,
      visited: 1 <= currentStep,
      onClick: navigateToVentilatedWallWallType,
    },
    {
      step: 2,
      component: RegularTab,
      text: t("navigation.ventilatedWall.facadeCladding" as any),
      selected: getFacadeCladdingTranslations(
        t,
        ventilatedWallState.configuration.facadeCladding.type,
      ),
      active: pathname === Routes.VENTILATED_WALL_2_FACADE_CLADDING,
      visited: 2 <= currentStep,
      onClick: navigateToVentilatedWallFacadeCladding,
    },
    {
      step: 3,
      component: OverviewTab,
      text: t("navigation.ventilatedWall.overview" as any),
      selected: null,
      active: pathname === Routes.VENTILATED_WALL_3_OVERVIEW,
      visited: 3 <= currentStep,
      onClick: navigateToVentilatedWallOverview,
    },
  ]

  // FLOOR
  const floorTabs: Tab[] = []
  floorTabs.push({
    step: 1,
    component: RegularTab,
    text: t("navigation.floor.floorHeating" as any),
    selected: getFloorHeatingTranslations(
      t,
      floorState.configuration.floorHeating.type,
    ),
    active: pathname === Routes.FLOOR_1_FLOOR_HEATING,
    visited: 1 <= currentStep,
    onClick: navigateToFloorHeating,
  })
  if (country !== "de") {
    floorTabs.push({
      step: 2,
      component: RegularTab,
      text: t("navigation.floor.floorInsulation" as any),
      selected: getFloorInsulationOptionTranslations(
        t,
        floorState.configuration.floorInsulation.type,
      ),
      active: pathname === Routes.FLOOR_2_FLOOR_INSULATION,
      visited: 2 <= currentStep,
      onClick: navigateToFloorInsulation,
    })
    floorTabs.push({
      step: 3,
      component: RegularTab,
      text: t("navigation.floor.floorScreed" as any),
      selected: getFloorScreedTranslations(
        t,
        floorState.configuration.floorScreed.type,
      ),
      active: pathname === Routes.FLOOR_3_FLOOR_SCREED,
      visited: 3 <= currentStep,
      onClick: navigateToFloorScreed,
    })
    floorTabs.push({
      step: 4,
      component: OverviewTab,
      text: t("navigation.floor.overview" as any),
      selected: null,
      active: pathname === Routes.FLOOR_4_OVERVIEW,
      visited: 4 <= currentStep,
      onClick: navigateToFloorOverview,
    })
  } else {
    floorTabs.push({
      step: 2,
      component: RegularTab,
      text: t("navigation.floor.floorScreed" as any),
      selected: getFloorScreedTranslations(
        t,
        floorState.configuration.floorScreed.type,
      ),
      active: pathname === Routes.FLOOR_3_FLOOR_SCREED,
      visited: 2 <= currentStep,
      onClick: navigateToFloorScreed,
    })
    floorTabs.push({
      step: 3,
      component: OverviewTab,
      text: t("navigation.floor.overview" as any),
      selected: null,
      active: pathname === Routes.FLOOR_4_OVERVIEW,
      visited: 3 <= currentStep,
      onClick: navigateToFloorOverview,
    })
  }

  // SOL
  let solTabs: Tab[] = [
    {
      step: 1,
      component: RegularTab,
      text: t("navigation.sol.solInsulation" as any),
      selected: null,
      active: pathname === Routes.SOL_1_INSULATION,
      visited: 1 <= currentStep,
      onClick: navigateToSolInsulation,
    },
    {
      step: 4,
      component: OverviewTab,
      text: t("navigation.sol.overview" as any),
      selected: null,
      active: pathname === Routes.SOL_4_OVERVIEW,
      visited: 4 <= currentStep,
      onClick: navigateToSolOverview,
    },
  ]
  let solRoutes = [Routes.SOL_1_INSULATION, Routes.SOL_4_OVERVIEW]
  switch (solState.configuration.solInsulation.type) {
    case SolInsulationTypes.ENERTHERM_CHAPE_TG:
      solTabs = [
        {
          step: 1,
          component: RegularTab,
          text: t("navigation.sol.solInsulation" as any),
          selected: getSolInsulationTranslations(
            t,
            solState.configuration.solInsulation.type,
          ),
          active: pathname === Routes.SOL_1_INSULATION,
          visited: 1 <= currentStep,
          onClick: navigateToSolInsulation,
        },
        {
          step: 2,
          component: RegularTab,
          text: t("navigation.sol.solNoiseLevel" as any),
          selected: getSolNoiseLevelTranslations(
            t,
            solState.configuration.solNoiseLevel.type,
          ),
          active: pathname === Routes.SOL_2_NOISE_LEVEL,
          visited: 2 <= currentStep,
          onClick: navigateToSolNoiseLevel,
        },
        {
          step: 3,
          component: RegularTab,
          text: t("navigation.sol.solAcousticLayer" as any),
          selected: getSolAcousticLayerTranslations(
            t,
            solState.configuration.solAcousticLayer.type,
          ),
          active: pathname === Routes.SOL_3_ACOUSTIC_LAYER,
          visited: 3 <= currentStep,
          onClick: navigateToSolAcousticLayer,
        },
        {
          step: 4,
          component: OverviewTab,
          text: t("navigation.sol.overview" as any),
          selected: null,
          active: pathname === Routes.SOL_4_OVERVIEW,
          visited: 4 <= currentStep,
          onClick: navigateToSolOverview,
        },
      ]
      solRoutes = [
        Routes.SOL_1_INSULATION,
        Routes.SOL_2_NOISE_LEVEL,
        Routes.SOL_3_ACOUSTIC_LAYER,
        Routes.SOL_4_OVERVIEW,
      ]
      break
    case SolInsulationTypes.ENERTHERM_KR_ALU_SOUSDALLAGE:
      solTabs = [
        {
          step: 1,
          component: RegularTab,
          text: t("navigation.sol.solInsulation" as any),
          selected: getSolInsulationTranslations(
            t,
            solState.configuration.solInsulation.type,
          ),
          active: pathname === Routes.SOL_1_INSULATION,
          visited: 1 <= currentStep,
          onClick: navigateToSolInsulation,
        },
        {
          step: 2,
          component: RegularTab,
          text: t("navigation.sol.solConformity" as any),
          selected: getSolConformityTranslations(
            t,
            solState.configuration.solConformity.type,
          ),
          active: pathname === Routes.SOL_2_CONFORMITY,
          visited: 2 <= currentStep,
          onClick: navigateToSolConformity,
        },
        {
          step: 3,
          component: RegularTab,
          text: t("navigation.sol.solBuildingType" as any),
          selected: getSolBuildingTypeTranslations(
            t,
            solState.configuration.solBuildingType.type,
          ),
          active: pathname === Routes.SOL_3_BUILDING_TYPE,
          visited: 3 <= currentStep,
          onClick: navigateToSolBuildingType,
        },
        {
          step: 4,
          component: OverviewTab,
          text: t("navigation.sol.overview" as any),
          selected: null,
          active: pathname === Routes.SOL_4_OVERVIEW,
          visited: 4 <= currentStep,
          onClick: navigateToSolOverview,
        },
      ]
      solRoutes = [
        Routes.SOL_1_INSULATION,
        Routes.SOL_2_CONFORMITY,
        Routes.SOL_3_BUILDING_TYPE,
        Routes.SOL_4_OVERVIEW,
      ]
      break
    case SolInsulationTypes.ENERTHERM_KR_ALU_DALLEPORTEE:
      solTabs = [
        {
          step: 1,
          component: RegularTab,
          text: t("navigation.sol.solInsulation" as any),
          selected: getSolInsulationTranslations(
            t,
            solState.configuration.solInsulation.type,
          ),
          active: pathname === Routes.SOL_1_INSULATION,
          visited: 1 <= currentStep,
          onClick: navigateToSolInsulation,
        },
        {
          step: 4,
          component: OverviewTab,
          text: t("navigation.sol.overview" as any),
          selected: null,
          active: pathname === Routes.SOL_4_OVERVIEW,
          visited: 4 <= currentStep,
          onClick: navigateToSolOverview,
        },
      ]
      solRoutes = [Routes.SOL_1_INSULATION, Routes.SOL_4_OVERVIEW]
      break
  }

  // TOIT EN PENTE
  let toitEnPenteTabs: Tab[] = [
    {
      step: 1,
      component: RegularTab,
      text: t("navigation.toitEnPente.toitStructure" as any),
      selected: getToitEnPenteStructureTranslations(
        t,
        toitEnPenteState.configuration.toitStructure.type,
      ),
      active: pathname === Routes.TOIT_EN_PENTE_1_STRUCTURE,
      visited: 1 <= currentStep,
      onClick: navigateToToitEnPenteStructure,
    },
    {
      step: 2,
      component: RegularTab,
      text: t("navigation.toitEnPente.toitRainCover" as any),
      selected: getToitEnPenteRainCoverTranslations(
        t,
        toitEnPenteState.configuration.toitRainCover.type,
      ),
      active: pathname === Routes.TOIT_EN_PENTE_2_RAIN_COVER,
      visited: 2 <= currentStep,
      onClick: navigateToToitEnPenteRainCover,
    },
    {
      step: 3,
      component: OverviewTab,
      text: t("navigation.toitEnPente.overview" as any),
      selected: null,
      active: pathname === Routes.TOIT_EN_PENTE_3_OVERVIEW,
      visited: 3 <= currentStep,
      onClick: navigateToToitEnPenteOverview,
    },
  ]
  let toitEnPenteRoutes = [
    Routes.TOIT_EN_PENTE_1_STRUCTURE,
    Routes.TOIT_EN_PENTE_2_RAIN_COVER,
    Routes.TOIT_EN_PENTE_3_OVERVIEW,
  ]

  let tabs: Tab[] = []

  // FLAT ROOF
  const flatRoofRoutes = [
    Routes.FLAT_ROOF_1_ROOF_TYPE,
    Routes.FLAT_ROOF_2_ROOF_FLOOR,
    Routes.FLAT_ROOF_3_WATERPROOFING,
    Routes.FLAT_ROOF_4_SECURING_METHODS,
    Routes.FLAT_ROOF_5_OVERVIEW,
  ]

  // CAVITY WALL
  const cavityWallRoutes = [
    Routes.CAVITY_WALL_1_WALL_TYPE,
    Routes.CAVITY_WALL_2_WALL_INSULATION,
    Routes.CAVITY_WALL_3_OVERVIEW,
  ]

  // SLOPED ROOF
  const slopedRoofRoutes = [
    Routes.SLOPED_ROOF_1_ROOF_TYPE,
    Routes.SLOPED_ROOF_2_ROOF_INSULATION,
    Routes.SLOPED_ROOF_3_OVERVIEW,
  ]

  // VENTILATED WALL
  const ventilatedWallRoutes = [
    Routes.VENTILATED_WALL_1_WALL_TYPE,
    Routes.VENTILATED_WALL_2_FACADE_CLADDING,
    Routes.VENTILATED_WALL_3_OVERVIEW,
  ]

  // FLOOR
  const floorRoutes = [
    Routes.FLOOR_1_FLOOR_HEATING,
    Routes.FLOOR_2_FLOOR_INSULATION,
    Routes.FLOOR_3_FLOOR_SCREED,
    Routes.FLOOR_4_OVERVIEW,
  ]

  if (flatRoofRoutes.find((x) => x === pathname)) {
    tabs = flatRoofTabs
  } else if (cavityWallRoutes.find((x) => x === pathname)) {
    tabs = cavityWallTabs
  } else if (slopedRoofRoutes.find((x) => x === pathname)) {
    tabs = slopedRoofTabs
  } else if (ventilatedWallRoutes.find((x) => x === pathname)) {
    tabs = ventilatedWallTabs
  } else if (floorRoutes.find((x) => x === pathname)) {
    tabs = floorTabs
  } else if (solRoutes.find((x) => x === pathname)) {
    tabs = solTabs
  } else if (toitEnPenteRoutes.find((x) => x === pathname)) {
    tabs = toitEnPenteTabs
  }

  return {
    tabs,
    currentStep,
    navigateToSolNoiseLevel,
  }
}
